import type { ListboxProps } from '@headlessui/react';
import React from 'react';
import { ArrowIcon } from './ArrowIcon';
import { Item } from './Item';
import type { ItemProps, Key } from './Item';
import { Content } from './SelectContent';
import { Trigger } from './Trigger';
import { useSelect } from './useSelect';

export interface SelectProps extends Omit<ListboxProps, 'children' | 'disabled' | 'onChange' | 'value'> {
  readonly maxVisibleContentItems?: number;
  readonly disabled?: boolean;
  readonly onChange?: (value: Key) => void;
  readonly value?: Key;
  readonly hasError?: boolean;
  readonly limitWidth?: boolean;
  readonly placeholder?: string;
  readonly items: (Omit<ItemProps, 'key'> & { readonly label?: string })[];
}

// eslint-disable-next-line functional/prefer-readonly-type
export const Select: React.FC<SelectProps> & { Item: React.FC<ItemProps> } = ({
  items,
  value,
  maxVisibleContentItems,
  disabled,
  onChange,
  hasError,
  limitWidth,
  placeholder,
  ...restProps
}) => {
  const {
    isMobileViewport,
    isSelectedItem,
    itemsWithKey,
    label,
    handleAction,
    Wrapper,
    wrapperProps,
    TriggerWrapper,
    triggerWrapperProps,
    ContentWrapper,
    contentWrapperProps,
    Component,
    triggerWidth,
    setTriggerWidth,
  } = useSelect({ items, value, disabled, onChange, wrapperProps: restProps });

  return (
    <Wrapper {...wrapperProps}>
      <TriggerWrapper {...triggerWrapperProps}>
        <Component
          as={Trigger}
          disabled={disabled}
          hasError={hasError}
          placeholder={placeholder}
          rightIcon={<ArrowIcon disabled={disabled} />}
          setTriggerWidth={setTriggerWidth}
          value={label}
        />
      </TriggerWrapper>
      <ContentWrapper {...contentWrapperProps}>
        <Content
          isMobileViewport={isMobileViewport}
          isSelectedItem={isSelectedItem}
          items={items}
          itemsWithKey={itemsWithKey}
          limitWidth={limitWidth}
          maxVisibleContentItems={maxVisibleContentItems}
          onAction={handleAction}
          placeholder={placeholder}
          triggerWidth={triggerWidth}
        />
      </ContentWrapper>
    </Wrapper>
  );
};

Select.Item = Item;
